// Agency Campaigns — Command Center, Campaign Library, Template Library, Analytics
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { csrfHeadersFor } from '../api';
import {
  Megaphone, ArrowLeft, RefreshCw, Plus, Users, CheckCircle,
  Clock, TrendingUp, BarChart3, Phone, MessageSquare, Mail,
  X, AlertTriangle, ChevronRight, Filter, Search,
  Play, Pause, Archive, Edit3, Eye, Trash2, Send, Target,
  Activity, DollarSign, Zap, ChevronDown, ChevronUp,
  Calendar, Settings, Check, ArrowRight, List, LayoutGrid
} from 'lucide-react';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// ============================================================================
// CONSTANTS
// ============================================================================

const TABS = [
  { id: 'command', label: 'Command Center', icon: Zap },
  { id: 'library', label: 'Campaign Library', icon: List },
  { id: 'templates', label: 'Template Library', icon: Edit3 },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 }
];

const STATUS_CONFIG = {
  draft:     { label: 'Draft',     dot: 'bg-gray-500',   badge: 'bg-gray-800 text-gray-300 border-gray-700' },
  active:    { label: 'Active',    dot: 'bg-emerald-500', badge: 'bg-emerald-900/40 text-emerald-400 border-emerald-700' },
  paused:    { label: 'Paused',    dot: 'bg-amber-500',   badge: 'bg-amber-900/40 text-amber-400 border-amber-700' },
  completed: { label: 'Completed', dot: 'bg-blue-500',    badge: 'bg-blue-900/40 text-blue-400 border-blue-700' }
};

const TYPE_LABELS = {
  birthday: 'Birthday', t65: 'T65', aep: 'AEP', oep: 'OEP',
  renewal: 'Renewal', save: 'Save Attempt', annual_review: 'Annual Review', custom: 'Custom'
};

const CHANNEL_CONFIG = {
  sms:   { icon: MessageSquare, color: 'text-emerald-400', label: 'SMS' },
  email: { icon: Mail,          color: 'text-blue-400',    label: 'Email' },
  call:  { icon: Phone,         color: 'text-amber-400',   label: 'Call' }
};

const MERGE_FIELDS = ['{{first_name}}', '{{last_name}}', '{{agent_name}}', '{{agent_phone}}', '{{carrier}}', '{{plan_name}}'];

const TEMPLATE_CATEGORIES = ['birthday', 't65', 'aep', 'oep', 'renewal', 'save', 'annual_review', 'general'];

// ============================================================================
// SMALL SHARED COMPONENTS
// ============================================================================

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function ChannelIcons({ channels = [] }) {
  const list = Array.isArray(channels) ? channels : [];
  return (
    <div className="flex items-center gap-1">
      {list.map(ch => {
        const cfg = CHANNEL_CONFIG[ch];
        if (!cfg) return null;
        const Icon = cfg.icon;
        return <Icon key={ch} size={13} className={cfg.color} title={cfg.label} />;
      })}
    </div>
  );
}

function StatCard({ label, value, sub, color = 'text-white', icon: Icon }) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
      {Icon && <Icon size={16} className="text-gray-500 mb-2" />}
      <p className={`text-2xl font-bold tabular-nums ${color}`}>{value ?? '—'}</p>
      <p className="text-xs text-gray-400 mt-0.5">{label}</p>
      {sub && <p className="text-xs text-gray-600 mt-0.5">{sub}</p>}
    </div>
  );
}

function ProgressBar({ value, max, color = 'bg-amber-400' }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="w-full h-1.5 bg-gray-800 rounded-full overflow-hidden">
      <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function EmptyState({ icon: Icon, title, body, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
      <Icon size={48} className="text-gray-700 mb-4" />
      <h3 className="text-lg font-semibold text-gray-300 mb-2">{title}</h3>
      <p className="text-sm text-gray-500 mb-6 max-w-sm">{body}</p>
      {action}
    </div>
  );
}

function Spinner({ size = 20 }) {
  return <RefreshCw size={size} className="animate-spin text-amber-400" />;
}

// ============================================================================
// TAB: COMMAND CENTER
// ============================================================================

function CommandCenter({ agencyId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/campaigns/command-center`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setData(json);
    } catch (err) {
      console.error('[CommandCenter]', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size={28} />
      </div>
    );
  }

  if (!data) {
    return (
      <EmptyState icon={Megaphone} title="No data available" body="Could not load command center data." />
    );
  }

  const { kpi, activeCampaigns, needsAttention, agentParticipation, activityFeed } = data;

  const activityLabel = (event) => {
    if (event.status === 'replied') return `Client replied via ${event.channel?.toUpperCase()} — ${event.campaignName}`;
    if (event.status === 'sent' || event.status === 'delivered') return `Message sent via ${event.channel?.toUpperCase()} — ${event.campaignName}`;
    if (event.status === 'failed') return `Message failed — ${event.campaignName}`;
    return `${event.status} — ${event.campaignName}`;
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div className="space-y-6">
      {/* KPI Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard label="Total Campaigns"    value={kpi.totalCampaigns}    icon={Megaphone} />
        <StatCard label="Active"             value={kpi.activeCampaigns}   icon={Play} color="text-emerald-400" />
        <StatCard label="Clients Reached"    value={kpi.clientsReached.toLocaleString()}    icon={Users} color="text-blue-400" />
        <StatCard label="Saves Attributed"   value={kpi.savesAttributed}   icon={CheckCircle} color="text-amber-400" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Active Campaigns */}
        <div className="xl:col-span-2 bg-gray-900 border border-gray-800 rounded-lg">
          <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-white">Active Campaigns</h2>
            <span className="text-xs text-gray-500">{activeCampaigns.length} running</span>
          </div>
          {activeCampaigns.length === 0 ? (
            <div className="px-5 py-10 text-center text-gray-500 text-sm">No active campaigns</div>
          ) : (
            <div className="divide-y divide-gray-800">
              {activeCampaigns.map(c => (
                <div key={c.id} className="px-5 py-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-white">{c.name}</span>
                        <ChannelIcons channels={c.channels} />
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {TYPE_LABELS[c.campaignType] || c.campaignType}
                        {c.daysRemaining !== null && (
                          <span className="ml-2 text-gray-600">· {c.daysRemaining > 0 ? `${c.daysRemaining}d left` : 'Ends today'}</span>
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-semibold text-amber-400 tabular-nums">{c.replyRate}%</span>
                      <p className="text-xs text-gray-600">reply rate</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <ProgressBar value={c.contactedCount} max={c.enrolledCount} />
                    <span className="text-xs text-gray-500 tabular-nums whitespace-nowrap">
                      {c.contactedCount}/{c.enrolledCount}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-2">
                    <span className="text-xs text-gray-600">Replies: <span className="text-gray-400">{c.repliedCount}</span></span>
                    <span className="text-xs text-gray-600">Saves: <span className="text-emerald-400 font-medium">{c.savesCount}</span></span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right column */}
        <div className="space-y-4">
          {/* Needs Attention */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg">
            <div className="px-5 py-4 border-b border-gray-800">
              <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                <AlertTriangle size={14} className="text-amber-400" />
                Needs Attention
              </h2>
            </div>
            <div className="divide-y divide-gray-800">
              {needsAttention.overdueCallTasks > 0 && (
                <div className="px-5 py-3 flex items-center justify-between">
                  <span className="text-sm text-gray-300">Overdue call tasks</span>
                  <span className="text-sm font-semibold text-red-400 tabular-nums">{needsAttention.overdueCallTasks}</span>
                </div>
              )}
              {needsAttention.belowAvgReplyCampaigns > 0 && (
                <div className="px-5 py-3 flex items-center justify-between">
                  <span className="text-sm text-gray-300">Campaigns below avg reply</span>
                  <span className="text-sm font-semibold text-amber-400 tabular-nums">{needsAttention.belowAvgReplyCampaigns}</span>
                </div>
              )}
              {needsAttention.awaitingFollowUp > 0 && (
                <div className="px-5 py-3 flex items-center justify-between">
                  <span className="text-sm text-gray-300">Replies awaiting follow-up</span>
                  <span className="text-sm font-semibold text-amber-400 tabular-nums">{needsAttention.awaitingFollowUp}</span>
                </div>
              )}
              {needsAttention.overdueCallTasks === 0 && needsAttention.belowAvgReplyCampaigns === 0 && needsAttention.awaitingFollowUp === 0 && (
                <div className="px-5 py-4 text-sm text-gray-500">All clear — nothing needs attention</div>
              )}
            </div>
          </div>

          {/* Live Activity Feed */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg">
            <div className="px-5 py-4 border-b border-gray-800">
              <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                <Activity size={14} className="text-emerald-400" />
                Live Activity
              </h2>
            </div>
            {activityFeed.length === 0 ? (
              <div className="px-5 py-6 text-sm text-gray-500 text-center">No recent activity</div>
            ) : (
              <div className="divide-y divide-gray-800">
                {activityFeed.map(event => (
                  <div key={event.id} className="px-5 py-3">
                    <p className="text-xs text-gray-300 leading-snug">{activityLabel(event)}</p>
                    <p className="text-xs text-gray-600 mt-0.5">{formatTime(event.timestamp)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Agent Participation */}
      {agentParticipation.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-lg">
          <div className="px-5 py-4 border-b border-gray-800">
            <h2 className="text-sm font-semibold text-white">Agent Participation</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left px-5 py-3 text-xs text-gray-500 font-medium">Agent</th>
                  <th className="text-right px-4 py-3 text-xs text-gray-500 font-medium">Assigned</th>
                  <th className="text-right px-4 py-3 text-xs text-gray-500 font-medium">Contacted</th>
                  <th className="text-right px-4 py-3 text-xs text-gray-500 font-medium">Responded</th>
                  <th className="text-right px-5 py-3 text-xs text-gray-500 font-medium">Saves</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {agentParticipation.map(a => {
                  const pct = a.assignedClients > 0
                    ? Math.round((a.contacted / a.assignedClients) * 100)
                    : 0;
                  return (
                    <tr key={a.agentId} className="hover:bg-gray-800/30">
                      <td className="px-5 py-3">
                        <span className="text-gray-200 font-medium">{a.agentName}</span>
                      </td>
                      <td className="text-right px-4 py-3 tabular-nums text-gray-400">{a.assignedClients}</td>
                      <td className="text-right px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <ProgressBar value={a.contacted} max={a.assignedClients} />
                          <span className={`tabular-nums text-xs font-medium ${pct < 50 ? 'text-red-400' : pct < 80 ? 'text-amber-400' : 'text-emerald-400'}`}>
                            {pct}%
                          </span>
                        </div>
                      </td>
                      <td className="text-right px-4 py-3 tabular-nums text-gray-400">{a.responded}</td>
                      <td className="text-right px-5 py-3 tabular-nums font-semibold text-amber-400">{a.saves}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// CAMPAIGN BUILDER WIZARD
// ============================================================================

const WIZARD_STEPS = ['Audience', 'Sequence', 'Schedule', 'Review'];

const BLANK_STEP = { id: Date.now(), channel: 'sms', templateId: '', body: '', subject: '', delayDays: 0, delayHours: 0, condition: 'always' };

function CampaignBuilder({ onClose, onSaved, editCampaign }) {
  const isEdit = !!editCampaign;
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [audienceCount, setAudienceCount] = useState(null);
  const [audienceLoading, setAudienceLoading] = useState(false);
  const [templates, setTemplates] = useState([]);

  const [form, setForm] = useState({
    name: editCampaign?.name || '',
    description: editCampaign?.description || '',
    campaignType: editCampaign?.campaignType || 'custom',
    channels: editCampaign?.channels || ['sms'],
    audienceFilters: editCampaign?.audienceFilters || { riskMin: '', riskMax: '', ageMin: '', ageMax: '', carrier: '' },
    sequenceSteps: editCampaign?.sequenceSteps?.length > 0 ? editCampaign.sequenceSteps : [{ ...BLANK_STEP, id: Date.now() }],
    startDate: editCampaign?.startDate ? editCampaign.startDate.split('T')[0] : '',
    endDate: editCampaign?.endDate ? editCampaign.endDate.split('T')[0] : '',
    maxSendsPerDay: editCampaign?.maxSendsPerDay || 200,
    blackoutStartTime: editCampaign?.blackoutStartTime || '21:00',
    blackoutEndTime: editCampaign?.blackoutEndTime || '09:00'
  });

  // Load templates for sequence step selector
  useEffect(() => {
    fetch(`${BASE}/api/campaigns/templates`, { credentials: 'include' })
      .then(r => r.json())
      .then(j => { if (j.ok) setTemplates(j.templates || []); })
      .catch(() => {});
  }, []);

  const previewAudience = useCallback(async () => {
    setAudienceLoading(true);
    try {
      const res = await fetch(`${BASE}/api/campaigns/preview-audience`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('POST') },
        body: JSON.stringify({ audienceFilters: form.audienceFilters })
      });
      const json = await res.json();
      if (json.ok) setAudienceCount(json.count);
    } catch { /* ignore */ }
    finally { setAudienceLoading(false); }
  }, [form.audienceFilters]);

  const addStep = () => {
    setForm(f => ({
      ...f,
      sequenceSteps: [...f.sequenceSteps, { ...BLANK_STEP, id: Date.now() }]
    }));
  };

  const removeStep = (idx) => {
    setForm(f => ({
      ...f,
      sequenceSteps: f.sequenceSteps.filter((_, i) => i !== idx)
    }));
  };

  const updateStep = (idx, updates) => {
    setForm(f => ({
      ...f,
      sequenceSteps: f.sequenceSteps.map((s, i) => i === idx ? { ...s, ...updates } : s)
    }));
  };

  const moveStep = (idx, dir) => {
    setForm(f => {
      const steps = [...f.sequenceSteps];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= steps.length) return f;
      [steps[idx], steps[newIdx]] = [steps[newIdx], steps[idx]];
      return { ...f, sequenceSteps: steps };
    });
  };

  const canAdvance = () => {
    if (step === 0) return form.name.trim().length > 0;
    if (step === 1) return form.sequenceSteps.length > 0;
    return true;
  };

  const handleSave = async (launchNow = false) => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name,
        description: form.description,
        campaignType: form.campaignType,
        channels: form.channels,
        audienceFilters: form.audienceFilters,
        sequenceSteps: form.sequenceSteps,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        blackoutStartTime: form.blackoutStartTime,
        blackoutEndTime: form.blackoutEndTime,
        maxSendsPerDay: parseInt(form.maxSendsPerDay),
        status: launchNow ? 'active' : 'draft'
      };

      const url = isEdit
        ? `${BASE}/api/campaigns/${editCampaign.id}`
        : `${BASE}/api/campaigns`;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || 'Save failed');

      // If launching, enroll audience
      if (launchNow && !isEdit) {
        await fetch(`${BASE}/api/campaigns/${json.campaign.id}/enroll`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('POST') },
          body: JSON.stringify({ audienceFilters: form.audienceFilters })
        });
      }

      onSaved(json.campaign);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const smsTemplates = templates.filter(t => t.channel === 'sms');
  const emailTemplates = templates.filter(t => t.channel === 'email');

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-3xl max-h-[92vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-lg font-bold text-white">{isEdit ? 'Edit Campaign' : 'New Campaign'}</h2>
            <p className="text-xs text-gray-500 mt-0.5">Step {step + 1} of {WIZARD_STEPS.length}: {WIZARD_STEPS[step]}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-lg">
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="px-6 py-3 border-b border-gray-800 shrink-0">
          <div className="flex items-center gap-1">
            {WIZARD_STEPS.map((s, i) => (
              <React.Fragment key={s}>
                <button
                  onClick={() => i < step && setStep(i)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    i === step ? 'bg-amber-400 text-gray-950' :
                    i < step  ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 cursor-pointer' :
                    'bg-gray-800 text-gray-600 cursor-default'
                  }`}
                >
                  {i < step ? <Check size={11} /> : <span className="tabular-nums">{i + 1}</span>}
                  {s}
                </button>
                {i < WIZARD_STEPS.length - 1 && (
                  <ChevronRight size={14} className="text-gray-700 shrink-0" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* ---- STEP 0: AUDIENCE ---- */}
          {step === 0 && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Campaign Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g., AEP 2026 Outreach"
                  className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Description</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Goal of this campaign..."
                  className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-amber-400/40 resize-none"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Campaign Type</label>
                  <select
                    value={form.campaignType}
                    onChange={e => setForm(f => ({ ...f, campaignType: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                  >
                    {Object.entries(TYPE_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Channels</label>
                  <div className="flex gap-2">
                    {['sms', 'email', 'call'].map(ch => {
                      const cfg = CHANNEL_CONFIG[ch];
                      const Icon = cfg.icon;
                      const sel = form.channels.includes(ch);
                      return (
                        <button
                          key={ch}
                          type="button"
                          onClick={() => {
                            const chs = sel ? form.channels.filter(c => c !== ch) : [...form.channels, ch];
                            if (chs.length > 0) setForm(f => ({ ...f, channels: chs }));
                          }}
                          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                            sel ? 'bg-amber-400/10 border-amber-400/50 text-amber-400' : 'bg-gray-800 border-gray-700 text-gray-500 hover:border-gray-600'
                          }`}
                        >
                          <Icon size={13} />
                          {cfg.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-400 mb-3">Audience Filters</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Risk Score Min (0–100)</label>
                    <input
                      type="number" min="0" max="100"
                      value={form.audienceFilters.riskMin}
                      onChange={e => setForm(f => ({ ...f, audienceFilters: { ...f.audienceFilters, riskMin: e.target.value } }))}
                      placeholder="0"
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Risk Score Max (0–100)</label>
                    <input
                      type="number" min="0" max="100"
                      value={form.audienceFilters.riskMax}
                      onChange={e => setForm(f => ({ ...f, audienceFilters: { ...f.audienceFilters, riskMax: e.target.value } }))}
                      placeholder="100"
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Age Min</label>
                    <input
                      type="number" min="0"
                      value={form.audienceFilters.ageMin}
                      onChange={e => setForm(f => ({ ...f, audienceFilters: { ...f.audienceFilters, ageMin: e.target.value } }))}
                      placeholder="e.g., 65"
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Age Max</label>
                    <input
                      type="number" min="0"
                      value={form.audienceFilters.ageMax}
                      onChange={e => setForm(f => ({ ...f, audienceFilters: { ...f.audienceFilters, ageMax: e.target.value } }))}
                      placeholder="e.g., 85"
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">Carrier (optional)</label>
                    <input
                      type="text"
                      value={form.audienceFilters.carrier}
                      onChange={e => setForm(f => ({ ...f, audienceFilters: { ...f.audienceFilters, carrier: e.target.value } }))}
                      placeholder="e.g., Humana, Cigna"
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between mt-3">
                  <button
                    type="button"
                    onClick={previewAudience}
                    className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1.5"
                    disabled={audienceLoading}
                  >
                    {audienceLoading ? <Spinner size={13} /> : <Target size={13} />}
                    Preview Audience Size
                  </button>
                  {audienceCount !== null && (
                    <span className="text-xs font-semibold text-white tabular-nums">
                      {audienceCount.toLocaleString()} matching clients
                    </span>
                  )}
                </div>
              </div>
            </>
          )}

          {/* ---- STEP 1: SEQUENCE ---- */}
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">
                Build a linear sequence of steps. Each step sends a message after a delay from the previous step.
              </p>

              {form.sequenceSteps.map((s, idx) => (
                <div key={s.id} className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-amber-400">Step {idx + 1}</span>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => moveStep(idx, -1)}
                        disabled={idx === 0}
                        className="p-1 text-gray-600 hover:text-gray-400 disabled:opacity-30"
                      >
                        <ChevronUp size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveStep(idx, 1)}
                        disabled={idx === form.sequenceSteps.length - 1}
                        className="p-1 text-gray-600 hover:text-gray-400 disabled:opacity-30"
                      >
                        <ChevronDown size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeStep(idx)}
                        disabled={form.sequenceSteps.length === 1}
                        className="p-1 text-gray-600 hover:text-red-400 disabled:opacity-30"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Channel</label>
                      <select
                        value={s.channel}
                        onChange={e => updateStep(idx, { channel: e.target.value })}
                        className="w-full px-2 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm text-white focus:outline-none"
                      >
                        <option value="sms">SMS</option>
                        <option value="email">Email</option>
                        <option value="call">Call Task</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Delay (Days)</label>
                      <input
                        type="number" min="0"
                        value={s.delayDays}
                        onChange={e => updateStep(idx, { delayDays: parseInt(e.target.value) || 0 })}
                        className="w-full px-2 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm text-white focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Condition</label>
                      <select
                        value={s.condition}
                        onChange={e => updateStep(idx, { condition: e.target.value })}
                        className="w-full px-2 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm text-white focus:outline-none"
                      >
                        <option value="always">Always</option>
                        <option value="no_reply">If no reply</option>
                      </select>
                    </div>
                  </div>

                  {/* Template selector */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Template (optional)</label>
                    <select
                      value={s.templateId}
                      onChange={e => {
                        const t = templates.find(t => t.id === e.target.value);
                        updateStep(idx, {
                          templateId: e.target.value,
                          body: t?.body || s.body,
                          subject: t?.subject || s.subject
                        });
                      }}
                      className="w-full px-2 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm text-white focus:outline-none"
                    >
                      <option value="">— Write custom message —</option>
                      {(s.channel === 'sms' ? smsTemplates : emailTemplates).map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Message body */}
                  {s.channel === 'email' && (
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Subject</label>
                      <input
                        type="text"
                        value={s.subject}
                        onChange={e => updateStep(idx, { subject: e.target.value })}
                        placeholder="Email subject line..."
                        className="w-full px-2 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      {s.channel === 'call' ? 'Call Script / Notes' : 'Message Body'}
                    </label>
                    <textarea
                      value={s.body}
                      onChange={e => updateStep(idx, { body: e.target.value })}
                      placeholder={s.channel === 'call' ? 'Notes for the agent...' : 'Hi {{first_name}},...'}
                      className="w-full px-2 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none resize-none"
                      rows={3}
                    />
                    {s.channel === 'sms' && (
                      <p className={`text-xs mt-1 ${s.body.length > 160 ? 'text-amber-400' : 'text-gray-600'}`}>
                        {s.body.length}/160 chars{s.body.length > 160 ? ' — will send as multi-part SMS' : ''}
                      </p>
                    )}
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addStep}
                className="w-full py-3 border border-dashed border-gray-700 rounded-lg text-sm text-gray-500 hover:border-amber-400/50 hover:text-amber-400 transition-colors flex items-center justify-center gap-2"
              >
                <Plus size={16} />
                Add Step
              </button>

              {/* Merge fields reference */}
              <div className="bg-gray-800/50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-2">Available merge fields:</p>
                <div className="flex flex-wrap gap-2">
                  {MERGE_FIELDS.map(f => (
                    <span key={f} className="px-2 py-0.5 bg-gray-700 rounded text-xs text-gray-300 font-mono">{f}</span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ---- STEP 2: SCHEDULE ---- */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Start Date</label>
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">End Date (optional)</label>
                  <input
                    type="date"
                    value={form.endDate}
                    onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Blackout Starts (no sends after)</label>
                  <input
                    type="time"
                    value={form.blackoutStartTime}
                    onChange={e => setForm(f => ({ ...f, blackoutStartTime: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Blackout Ends (sends resume after)</label>
                  <input
                    type="time"
                    value={form.blackoutEndTime}
                    onChange={e => setForm(f => ({ ...f, blackoutEndTime: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Max Sends Per Day</label>
                <input
                  type="number" min="1" max="10000"
                  value={form.maxSendsPerDay}
                  onChange={e => setForm(f => ({ ...f, maxSendsPerDay: parseInt(e.target.value) || 200 }))}
                  className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                />
                <p className="text-xs text-gray-600 mt-1">Spread sends over the day to avoid carrier spam filtering</p>
              </div>

              <div className="bg-amber-400/5 border border-amber-400/20 rounded-lg p-4">
                <p className="text-xs font-medium text-amber-400 mb-1">TCPA Compliance Reminder</p>
                <p className="text-xs text-gray-400">
                  All SMS messages must be sent between 8 AM – 9 PM recipient local time per TCPA regulations.
                  Blackout windows help ensure compliance. Campaign messages require prior express written consent.
                </p>
              </div>
            </div>
          )}

          {/* ---- STEP 3: REVIEW ---- */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="bg-gray-800 rounded-lg p-4 space-y-3">
                <p className="text-xs font-semibold text-gray-300 uppercase tracking-wider">Campaign Summary</p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div><span className="text-gray-500">Name:</span> <span className="text-white ml-1">{form.name}</span></div>
                  <div><span className="text-gray-500">Type:</span> <span className="text-white ml-1">{TYPE_LABELS[form.campaignType]}</span></div>
                  <div><span className="text-gray-500">Channels:</span> <span className="text-white ml-1">{form.channels.map(c => CHANNEL_CONFIG[c]?.label).join(', ')}</span></div>
                  <div><span className="text-gray-500">Steps:</span> <span className="text-white ml-1">{form.sequenceSteps.length}</span></div>
                  {form.startDate && <div><span className="text-gray-500">Start:</span> <span className="text-white ml-1">{form.startDate}</span></div>}
                  {form.endDate && <div><span className="text-gray-500">End:</span> <span className="text-white ml-1">{form.endDate}</span></div>}
                  <div><span className="text-gray-500">Max/Day:</span> <span className="text-white ml-1">{form.maxSendsPerDay}</span></div>
                  {audienceCount !== null && (
                    <div><span className="text-gray-500">Audience:</span> <span className="text-amber-400 font-semibold ml-1">{audienceCount.toLocaleString()} clients</span></div>
                  )}
                </div>
              </div>

              {/* Audience filters summary */}
              {(form.audienceFilters.riskMin || form.audienceFilters.riskMax || form.audienceFilters.ageMin || form.audienceFilters.ageMax || form.audienceFilters.carrier) && (
                <div className="bg-gray-800 rounded-lg p-4">
                  <p className="text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2">Audience Filters</p>
                  <div className="flex flex-wrap gap-2">
                    {form.audienceFilters.riskMin && <span className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-300">Risk ≥ {form.audienceFilters.riskMin}</span>}
                    {form.audienceFilters.riskMax && <span className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-300">Risk ≤ {form.audienceFilters.riskMax}</span>}
                    {form.audienceFilters.ageMin && <span className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-300">Age ≥ {form.audienceFilters.ageMin}</span>}
                    {form.audienceFilters.ageMax && <span className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-300">Age ≤ {form.audienceFilters.ageMax}</span>}
                    {form.audienceFilters.carrier && <span className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-300">Carrier: {form.audienceFilters.carrier}</span>}
                  </div>
                </div>
              )}

              {/* Sequence steps summary */}
              <div className="bg-gray-800 rounded-lg p-4">
                <p className="text-xs font-semibold text-gray-300 uppercase tracking-wider mb-3">Sequence ({form.sequenceSteps.length} steps)</p>
                <div className="space-y-2">
                  {form.sequenceSteps.map((s, i) => {
                    const ch = CHANNEL_CONFIG[s.channel];
                    const Icon = ch?.icon || Send;
                    return (
                      <div key={s.id} className="flex items-start gap-3">
                        <div className="flex items-center gap-1 w-24 shrink-0">
                          <Icon size={12} className={ch?.color || 'text-gray-400'} />
                          <span className="text-xs text-gray-400">{ch?.label}</span>
                        </div>
                        <div className="flex-1">
                          <span className="text-xs text-gray-500">Day {s.delayDays}</span>
                          {s.condition === 'no_reply' && <span className="ml-2 text-xs text-amber-400/70">(if no reply)</span>}
                          {s.body && <p className="text-xs text-gray-300 mt-0.5 line-clamp-1">{s.body}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Compliance checklist */}
              <div className="bg-gray-800 rounded-lg p-4">
                <p className="text-xs font-semibold text-gray-300 uppercase tracking-wider mb-3">Compliance Checklist</p>
                <div className="space-y-2">
                  {[
                    'Clients have provided prior express written consent for SMS marketing',
                    'Messages include opt-out instructions (STOP to unsubscribe)',
                    'Send times respect TCPA 8 AM – 9 PM local time requirement',
                    'Messages are accurate and do not contain misleading Medicare benefit claims',
                    'Campaign is not targeted at plan-specific non-enrolled beneficiaries'
                  ].map(item => (
                    <label key={item} className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" className="mt-0.5 shrink-0 accent-amber-400" />
                      <span className="text-xs text-gray-400">{item}</span>
                    </label>
                  ))}
                </div>
              </div>

              {error && (
                <div className="bg-red-900/30 border border-red-700 rounded-lg p-3">
                  <p className="text-xs text-red-400">{error}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-800 flex items-center justify-between shrink-0">
          <button
            type="button"
            onClick={() => step === 0 ? onClose() : setStep(s => s - 1)}
            className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
          >
            {step === 0 ? 'Cancel' : 'Back'}
          </button>

          <div className="flex items-center gap-3">
            {step === WIZARD_STEPS.length - 1 ? (
              <>
                <button
                  type="button"
                  onClick={() => handleSave(false)}
                  disabled={saving || !canAdvance()}
                  className="px-4 py-2 text-sm bg-gray-700 text-gray-200 rounded-lg hover:bg-gray-600 disabled:opacity-50 font-medium transition-colors"
                >
                  {saving ? 'Saving...' : 'Save as Draft'}
                </button>
                <button
                  type="button"
                  onClick={() => handleSave(true)}
                  disabled={saving || !canAdvance()}
                  className="px-5 py-2 text-sm bg-amber-400 text-gray-950 rounded-lg hover:bg-amber-300 disabled:opacity-50 font-bold transition-colors flex items-center gap-2"
                >
                  <Play size={14} />
                  {saving ? 'Launching...' : 'Launch Campaign'}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setStep(s => s + 1)}
                disabled={!canAdvance()}
                className="px-5 py-2 text-sm bg-amber-400 text-gray-950 rounded-lg hover:bg-amber-300 disabled:opacity-50 font-bold transition-colors flex items-center gap-2"
              >
                Next
                <ChevronRight size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// TAB: CAMPAIGN LIBRARY
// ============================================================================

function CampaignLibrary({ onCreateNew }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [showBuilder, setShowBuilder] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterType) params.set('campaign_type', filterType);
      const res = await fetch(`${BASE}/api/campaigns?${params}`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setCampaigns(json.campaigns || []);
    } catch (err) {
      console.error('[CampaignLibrary]', err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (id, status) => {
    try {
      await fetch(`${BASE}/api/campaigns/${id}/status`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('PATCH') },
        body: JSON.stringify({ status })
      });
      load();
    } catch { /* ignore */ }
  };

  const filtered = campaigns.filter(c =>
    !searchQuery || c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-5">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search campaigns..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-amber-400/40"
          />
        </div>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-sm text-gray-300 focus:outline-none"
        >
          <option value="">All Statuses</option>
          {Object.entries(STATUS_CONFIG).map(([v, c]) => (
            <option key={v} value={v}>{c.label}</option>
          ))}
        </select>
        <select
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
          className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-sm text-gray-300 focus:outline-none"
        >
          <option value="">All Types</option>
          {Object.entries(TYPE_LABELS).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <button onClick={load} className="p-2 hover:bg-gray-800 rounded-lg">
          <RefreshCw size={16} className="text-gray-500" />
        </button>
        <button
          onClick={onCreateNew}
          className="flex items-center gap-2 px-4 py-2 bg-amber-400 text-gray-950 rounded-lg text-sm font-bold hover:bg-amber-300 transition-colors"
        >
          <Plus size={16} />
          New Campaign
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Spinner size={28} /></div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Megaphone}
          title="No campaigns found"
          body="Create your first campaign to start reaching clients at scale."
          action={
            <button
              onClick={onCreateNew}
              className="flex items-center gap-2 px-4 py-2 bg-amber-400 text-gray-950 rounded-lg text-sm font-bold hover:bg-amber-300"
            >
              <Plus size={16} /> Create Campaign
            </button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(c => (
            <CampaignCard
              key={c.id}
              campaign={c}
              onEdit={() => { setEditingCampaign(c); setShowBuilder(true); }}
              onStatusChange={handleStatusChange}
              onRefresh={load}
            />
          ))}
        </div>
      )}

      {showBuilder && (
        <CampaignBuilder
          editCampaign={editingCampaign}
          onClose={() => { setShowBuilder(false); setEditingCampaign(null); }}
          onSaved={() => { setShowBuilder(false); setEditingCampaign(null); load(); }}
        />
      )}
    </div>
  );
}

function CampaignCard({ campaign: c, onEdit, onStatusChange, onRefresh }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const replyRate = c.replyRate || 0;
  const saveRate = c.enrolledCount > 0 ? Math.round((c.savesCount / c.enrolledCount) * 100) : 0;

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-5 flex flex-col gap-4 hover:border-gray-700 transition-colors">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <StatusBadge status={c.status} />
            <span className="text-xs text-gray-600">{TYPE_LABELS[c.campaignType] || c.campaignType}</span>
          </div>
          <h3 className="text-sm font-semibold text-white leading-snug">{c.name}</h3>
          {c.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{c.description}</p>}
        </div>
        <div ref={menuRef} className="relative shrink-0">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="p-1.5 hover:bg-gray-800 rounded-lg text-gray-500 hover:text-gray-300"
          >
            <Settings size={14} />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-8 w-44 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-20 py-1">
              <button onClick={() => { onEdit(); setMenuOpen(false); }}
                className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2">
                <Edit3 size={13} /> Edit
              </button>
              {c.status === 'draft' && (
                <button onClick={() => { onStatusChange(c.id, 'active'); setMenuOpen(false); }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2">
                  <Play size={13} /> Activate
                </button>
              )}
              {c.status === 'active' && (
                <button onClick={() => { onStatusChange(c.id, 'paused'); setMenuOpen(false); }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2">
                  <Pause size={13} /> Pause
                </button>
              )}
              {c.status === 'paused' && (
                <button onClick={() => { onStatusChange(c.id, 'active'); setMenuOpen(false); }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2">
                  <Play size={13} /> Resume
                </button>
              )}
              {c.status !== 'completed' && (
                <button onClick={() => { onStatusChange(c.id, 'completed'); setMenuOpen(false); }}
                  className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-gray-700 flex items-center gap-2">
                  <Archive size={13} /> Archive
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Channel icons */}
      <div className="flex items-center gap-1.5">
        <ChannelIcons channels={c.channels} />
        {c.startDate && (
          <span className="text-xs text-gray-600 ml-auto">
            Started {new Date(c.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        )}
      </div>

      {/* Progress */}
      {c.enrolledCount > 0 && (
        <div>
          <div className="flex justify-between text-xs text-gray-500 mb-1.5">
            <span>Contacted</span>
            <span className="tabular-nums">{c.contactedCount} / {c.enrolledCount}</span>
          </div>
          <ProgressBar value={c.contactedCount} max={c.enrolledCount} />
        </div>
      )}

      {/* Metrics */}
      <div className="grid grid-cols-4 gap-2">
        <div className="text-center">
          <p className="text-lg font-bold tabular-nums text-white">{c.enrolledCount}</p>
          <p className="text-xs text-gray-600">Enrolled</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold tabular-nums text-blue-400">{replyRate}%</p>
          <p className="text-xs text-gray-600">Reply</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold tabular-nums text-emerald-400">{c.savesCount}</p>
          <p className="text-xs text-gray-600">Saves</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold tabular-nums text-amber-400">{saveRate}%</p>
          <p className="text-xs text-gray-600">Save Rate</p>
        </div>
      </div>

      {c.daysRemaining !== null && c.status === 'active' && (
        <p className="text-xs text-gray-600">
          {c.daysRemaining > 0 ? `${c.daysRemaining} days remaining` : c.daysRemaining === 0 ? 'Ends today' : 'Overdue'}
        </p>
      )}
    </div>
  );
}

// ============================================================================
// TEMPLATE EDITOR MODAL
// ============================================================================

function TemplateEditor({ template, onClose, onSaved }) {
  const isEdit = !!template;
  const [form, setForm] = useState({
    name: template?.name || '',
    description: template?.description || '',
    category: template?.category || 'general',
    channel: template?.channel || 'sms',
    subject: template?.subject || '',
    body: template?.body || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const insertMergeField = (field) => {
    setForm(f => ({ ...f, body: f.body + field }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const url = isEdit
        ? `${BASE}/api/campaigns/templates/${template.id}`
        : `${BASE}/api/campaigns/templates`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          description: form.description,
          category: form.category,
          channel: form.channel,
          subject: form.subject || null,
          body: form.body,
          mergeFields: MERGE_FIELDS.filter(f => form.body.includes(f) || (form.subject && form.subject.includes(f)))
        })
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || 'Save failed');
      onSaved(json.template);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
        <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between shrink-0">
          <h2 className="text-lg font-bold text-white">{isEdit ? 'Edit Template' : 'New Template'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-lg">
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Template Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g., AEP Outreach SMS"
              className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-amber-400/40"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Category</label>
              <select
                value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none"
              >
                {TEMPLATE_CATEGORIES.map(c => (
                  <option key={c} value={c}>{TYPE_LABELS[c] || c.charAt(0).toUpperCase() + c.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Channel</label>
              <select
                value={form.channel}
                onChange={e => setForm(f => ({ ...f, channel: e.target.value }))}
                className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white focus:outline-none"
              >
                <option value="sms">SMS</option>
                <option value="email">Email</option>
                <option value="call">Call Script</option>
              </select>
            </div>
          </div>

          {form.channel === 'email' && (
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Subject Line</label>
              <input
                type="text"
                value={form.subject}
                onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                placeholder="Email subject..."
                className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-amber-400/40"
              />
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-gray-400">Message Body *</label>
              {form.channel === 'sms' && (
                <span className={`text-xs tabular-nums ${form.body.length > 160 ? 'text-amber-400' : 'text-gray-600'}`}>
                  {form.body.length}/160
                </span>
              )}
            </div>
            <textarea
              value={form.body}
              onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
              placeholder="Hi {{first_name}},..."
              className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-amber-400/40 resize-none"
              rows={6}
            />
            {form.channel === 'sms' && form.body.length > 160 && (
              <p className="text-xs text-amber-400 mt-1">Message exceeds 160 characters and will be sent as a multi-part SMS.</p>
            )}
          </div>

          {/* Merge fields */}
          <div>
            <p className="text-xs font-medium text-gray-400 mb-2">Insert Merge Field</p>
            <div className="flex flex-wrap gap-2">
              {MERGE_FIELDS.map(f => (
                <button
                  key={f}
                  type="button"
                  onClick={() => insertMergeField(f)}
                  className="px-2 py-1 bg-gray-800 border border-gray-700 hover:border-amber-400/50 rounded text-xs text-gray-300 font-mono transition-colors"
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          {/* Preview */}
          {form.body && (
            <div>
              <p className="text-xs font-medium text-gray-400 mb-2">Preview</p>
              <div className={`bg-gray-800 rounded-lg p-4 ${form.channel === 'sms' ? 'max-w-xs' : 'w-full'}`}>
                {form.channel === 'email' && form.subject && (
                  <p className="text-xs font-semibold text-gray-200 mb-2 pb-2 border-b border-gray-700">{form.subject}</p>
                )}
                <p className="text-xs text-gray-300 whitespace-pre-wrap leading-relaxed">
                  {form.body
                    .replace(/{{first_name}}/g, 'Jane')
                    .replace(/{{last_name}}/g, 'Smith')
                    .replace(/{{agent_name}}/g, 'Your Agent')
                    .replace(/{{agent_phone}}/g, '(555) 123-4567')
                    .replace(/{{carrier}}/g, 'Humana')
                    .replace(/{{plan_name}}/g, 'Gold Plus HMO')}
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-900/30 border border-red-700 rounded-lg p-3">
              <p className="text-xs text-red-400">{error}</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-800 flex items-center justify-end gap-3 shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving || !form.name.trim() || !form.body.trim()}
            className="px-5 py-2 text-sm bg-amber-400 text-gray-950 rounded-lg font-bold hover:bg-amber-300 disabled:opacity-50 transition-colors flex items-center gap-2"
          >
            {saving ? <Spinner size={14} /> : <Check size={14} />}
            {saving ? 'Saving...' : 'Save Template'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// TAB: TEMPLATE LIBRARY
// ============================================================================

function TemplateLibrary() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterChannel, setFilterChannel] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCategory) params.set('category', filterCategory);
      if (filterChannel) params.set('channel', filterChannel);
      const res = await fetch(`${BASE}/api/campaigns/templates?${params}`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setTemplates(json.templates || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [filterCategory, filterChannel]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Archive this template?')) return;
    try {
      await fetch(`${BASE}/api/campaigns/templates/${id}`, { method: 'DELETE', credentials: 'include', headers: { ...csrfHeadersFor('DELETE') } });
      load();
    } catch { /* ignore */ }
  };

  const openNew = () => { setEditingTemplate(null); setShowEditor(true); };
  const openEdit = (t) => { setEditingTemplate(t); setShowEditor(true); };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={filterCategory}
          onChange={e => setFilterCategory(e.target.value)}
          className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-sm text-gray-300 focus:outline-none"
        >
          <option value="">All Categories</option>
          {TEMPLATE_CATEGORIES.map(c => (
            <option key={c} value={c}>{TYPE_LABELS[c] || c}</option>
          ))}
        </select>
        <select
          value={filterChannel}
          onChange={e => setFilterChannel(e.target.value)}
          className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-sm text-gray-300 focus:outline-none"
        >
          <option value="">All Channels</option>
          {Object.entries(CHANNEL_CONFIG).map(([v, c]) => (
            <option key={v} value={v}>{c.label}</option>
          ))}
        </select>
        <button onClick={load} className="p-2 hover:bg-gray-800 rounded-lg">
          <RefreshCw size={16} className="text-gray-500" />
        </button>
        <button
          onClick={openNew}
          className="ml-auto flex items-center gap-2 px-4 py-2 bg-amber-400 text-gray-950 rounded-lg text-sm font-bold hover:bg-amber-300 transition-colors"
        >
          <Plus size={16} /> New Template
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Spinner size={28} /></div>
      ) : templates.length === 0 ? (
        <EmptyState
          icon={Edit3}
          title="No templates found"
          body="Create reusable message templates for your campaigns."
          action={
            <button onClick={openNew} className="flex items-center gap-2 px-4 py-2 bg-amber-400 text-gray-950 rounded-lg text-sm font-bold hover:bg-amber-300">
              <Plus size={16} /> Create Template
            </button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {templates.map(t => {
            const chCfg = CHANNEL_CONFIG[t.channel];
            const ChIcon = chCfg?.icon || Send;
            return (
              <div key={t.id} className="bg-gray-900 border border-gray-800 rounded-lg p-5 flex flex-col gap-3 hover:border-gray-700 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <ChIcon size={13} className={chCfg?.color || 'text-gray-400'} />
                      <span className="text-xs text-gray-500">{TYPE_LABELS[t.category] || t.category}</span>
                      {t.isDefault && (
                        <span className="px-1.5 py-0.5 bg-gray-800 rounded text-xs text-gray-500 border border-gray-700">Default</span>
                      )}
                    </div>
                    <h3 className="text-sm font-semibold text-white leading-snug">{t.name}</h3>
                    {t.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{t.description}</p>}
                  </div>
                  {!t.isDefault && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => openEdit(t)} className="p-1.5 hover:bg-gray-800 rounded text-gray-600 hover:text-gray-300">
                        <Edit3 size={13} />
                      </button>
                      <button onClick={() => handleDelete(t.id)} className="p-1.5 hover:bg-gray-800 rounded text-gray-600 hover:text-red-400">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                </div>

                <p className="text-xs text-gray-400 line-clamp-3 leading-relaxed flex-1">{t.body}</p>

                <div className="flex items-center gap-3 pt-2 border-t border-gray-800">
                  <div className="flex items-center gap-1.5">
                    <Send size={11} className="text-gray-600" />
                    <span className="text-xs text-gray-500 tabular-nums">{t.usageCount} uses</span>
                  </div>
                  {t.usageCount > 0 && (
                    <div className="flex items-center gap-1.5">
                      <MessageSquare size={11} className="text-gray-600" />
                      <span className="text-xs text-gray-500 tabular-nums">{t.replyRate}% reply</span>
                    </div>
                  )}
                  <span className="text-xs text-gray-700 ml-auto">
                    {new Date(t.updatedAt || t.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showEditor && (
        <TemplateEditor
          template={editingTemplate}
          onClose={() => { setShowEditor(false); setEditingTemplate(null); }}
          onSaved={() => { setShowEditor(false); setEditingTemplate(null); load(); }}
        />
      )}
    </div>
  );
}

// ============================================================================
// TAB: ANALYTICS
// ============================================================================

function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [sortBy, setSortBy] = useState('savesCount');
  const [sortDir, setSortDir] = useState('desc');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ dateFrom, dateTo });
      const res = await fetch(`${BASE}/api/campaigns/analytics/overview?${params}`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setData(json);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const setPreset = (days) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    setDateTo(to.toISOString().split('T')[0]);
    setDateFrom(from.toISOString().split('T')[0]);
  };

  const sortedCampaigns = data ? [...data.campaigns].sort((a, b) => {
    const va = a[sortBy] || 0;
    const vb = b[sortBy] || 0;
    return sortDir === 'asc' ? va - vb : vb - va;
  }) : [];

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ChevronDown size={12} className="text-gray-700" />;
    return sortDir === 'asc'
      ? <ChevronUp size={12} className="text-amber-400" />
      : <ChevronDown size={12} className="text-amber-400" />;
  };

  const maxSent = data ? Math.max(1, ...data.channelEffectiveness.map(c => c.totalSent)) : 1;

  return (
    <div className="space-y-6">
      {/* Date controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1">
          {[7, 30, 90].map(d => (
            <button
              key={d}
              onClick={() => setPreset(d)}
              className="px-3 py-1.5 text-xs bg-gray-900 border border-gray-800 rounded-lg text-gray-400 hover:border-amber-400/50 hover:text-amber-400 transition-colors"
            >
              {d}D
            </button>
          ))}
        </div>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded-lg text-sm text-gray-300 focus:outline-none" />
        <span className="text-gray-600 text-xs">to</span>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          className="px-3 py-1.5 bg-gray-900 border border-gray-800 rounded-lg text-sm text-gray-300 focus:outline-none" />
        <button onClick={load} className="p-2 hover:bg-gray-800 rounded-lg">
          <RefreshCw size={16} className="text-gray-500" />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Spinner size={28} /></div>
      ) : !data ? (
        <EmptyState icon={BarChart3} title="No analytics data" body="Run campaigns to see analytics." />
      ) : (
        <>
          {/* KPI Tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            <StatCard label="Total Sends" value={data.kpi.totalSends.toLocaleString()} icon={Send} />
            <StatCard label="Delivery Rate" value={`${data.kpi.deliveryRate}%`} icon={CheckCircle} color="text-emerald-400" />
            <StatCard label="Reply Rate" value={`${data.kpi.replyRate}%`} icon={MessageSquare} color="text-blue-400" />
            <StatCard label="Saves Attributed" value={data.kpi.savesAttributed} icon={TrendingUp} color="text-amber-400" />
            <StatCard label="Revenue Protected" value={data.kpi.revenueProtected > 0 ? `$${data.kpi.revenueProtected.toLocaleString()}` : '—'} icon={DollarSign} color="text-amber-400" />
          </div>

          {/* Channel Effectiveness */}
          {data.channelEffectiveness.length > 0 && (
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-5">
              <h3 className="text-sm font-semibold text-white mb-4">Channel Effectiveness</h3>
              <div className="space-y-4">
                {data.channelEffectiveness.map(ch => {
                  const cfg = CHANNEL_CONFIG[ch.channel];
                  const Icon = cfg?.icon || Send;
                  const widthPct = Math.round((ch.totalSent / maxSent) * 100);
                  return (
                    <div key={ch.channel}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <Icon size={14} className={cfg?.color || 'text-gray-400'} />
                          <span className="text-sm text-gray-300">{cfg?.label || ch.channel}</span>
                        </div>
                        <div className="flex items-center gap-4 text-xs tabular-nums">
                          <span className="text-gray-500">{ch.totalSent.toLocaleString()} sent</span>
                          <span className="font-semibold text-white">{ch.replyRate}% reply</span>
                        </div>
                      </div>
                      <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${cfg?.color?.replace('text-', 'bg-') || 'bg-gray-500'}`}
                          style={{ width: `${widthPct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Campaign Comparison Table */}
          {sortedCampaigns.length > 0 && (
            <div className="bg-gray-900 border border-gray-800 rounded-lg">
              <div className="px-5 py-4 border-b border-gray-800">
                <h3 className="text-sm font-semibold text-white">Campaign Comparison</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left px-5 py-3 text-xs text-gray-500 font-medium">Campaign</th>
                      {[
                        ['enrolledCount', 'Enrolled'],
                        ['totalSent', 'Sent'],
                        ['replyRate', 'Reply %'],
                        ['savesCount', 'Saves'],
                        ['saveRate', 'Save %'],
                        ['attributedRevenue', 'Revenue']
                      ].map(([col, label]) => (
                        <th key={col} className="text-right px-4 py-3 text-xs text-gray-500 font-medium cursor-pointer hover:text-gray-300 select-none"
                          onClick={() => handleSort(col)}>
                          <span className="flex items-center justify-end gap-1">
                            {label} <SortIcon col={col} />
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {sortedCampaigns.map(c => (
                      <tr key={c.id} className="hover:bg-gray-800/30">
                        <td className="px-5 py-3">
                          <div>
                            <span className="text-gray-200 font-medium text-sm">{c.name}</span>
                            <StatusBadge status={c.status} />
                          </div>
                        </td>
                        <td className="text-right px-4 py-3 tabular-nums text-gray-400">{c.enrolledCount}</td>
                        <td className="text-right px-4 py-3 tabular-nums text-gray-400">{c.totalSent.toLocaleString()}</td>
                        <td className="text-right px-4 py-3 tabular-nums font-medium text-blue-400">{c.replyRate}%</td>
                        <td className="text-right px-4 py-3 tabular-nums font-semibold text-emerald-400">{c.savesCount}</td>
                        <td className="text-right px-4 py-3 tabular-nums text-amber-400">{c.saveRate}%</td>
                        <td className="text-right px-5 py-3 tabular-nums text-gray-400">
                          {c.attributedRevenue > 0 ? `$${Math.round(c.attributedRevenue).toLocaleString()}` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {sortedCampaigns.length === 0 && (
            <EmptyState icon={BarChart3} title="No campaign data" body="No campaigns found for this date range." />
          )}
        </>
      )}
    </div>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export default function AgencyCampaigns() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('command');
  const [showBuilder, setShowBuilder] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleSaved = () => {
    setShowBuilder(false);
    setRefreshKey(k => k + 1);
    setActiveTab('library');
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-800 px-6 py-4">
        <div className="max-w-screen-xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/agency/dashboard')}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <ArrowLeft size={18} className="text-gray-400" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Megaphone size={20} className="text-amber-400" />
                Campaigns
              </h1>
              <p className="text-xs text-gray-500 mt-0.5">Agency-wide outreach campaigns and message templates</p>
            </div>
          </div>
          <button
            onClick={() => setShowBuilder(true)}
            className="flex items-center gap-2 px-4 py-2 bg-amber-400 text-gray-950 rounded-lg text-sm font-bold hover:bg-amber-300 transition-colors"
          >
            <Plus size={16} />
            New Campaign
          </button>
        </div>
      </div>

      {/* Tab Nav */}
      <div className="bg-gray-900 border-b border-gray-800 px-6">
        <div className="max-w-screen-xl mx-auto flex items-center gap-1">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  active
                    ? 'border-amber-400 text-amber-400'
                    : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-600'
                }`}
              >
                <Icon size={15} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-screen-xl mx-auto p-6">
        {activeTab === 'command' && <CommandCenter key={refreshKey} />}
        {activeTab === 'library' && (
          <CampaignLibrary
            key={refreshKey}
            onCreateNew={() => setShowBuilder(true)}
          />
        )}
        {activeTab === 'templates' && <TemplateLibrary key={refreshKey} />}
        {activeTab === 'analytics' && <Analytics key={refreshKey} />}
      </div>

      {/* Global Campaign Builder */}
      {showBuilder && (
        <CampaignBuilder
          onClose={() => setShowBuilder(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
